import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Megaphone, X, Loader2, Radio, Send, ChevronDown, Search, Check, Users, Car } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ONLINE_WINDOW_MS = 5 * 60 * 1000; // 5 min

const isOnline = (d) => {
  if (!d?.last_seen) return false;
  const t = new Date(d.last_seen).getTime();
  return Number.isFinite(t) && Date.now() - t < ONLINE_WINDOW_MS;
};

const initials = (name = '') => {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]).join('').toUpperCase() || 'D';
};

const avatarBg = (id = '') => {
  // Deterministic pastel-on-dark colour per driver
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) % 360;
  return `hsl(${h}, 70%, 35%)`;
};

/**
 * BroadcastButton — Uber-style admin broadcast.
 * - Multi-select dropdown showing online drivers (with avatars, vehicle, status dot).
 * - When 0 selected: behaves as legacy "broadcast to all" with online-only toggle.
 * - When drivers selected: targets exactly that subset.
 */
const BroadcastButton = ({ authHeaders, variant = 'sidebar' }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('Dispatch announcement');
  const [text, setText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  // Default to ALL drivers (online + offline). Admins toggle this on
  // explicitly when they only want online drivers.
  const [onlineOnly, setOnlineOnly] = useState(false);
  // Audience selector: 'drivers' | 'customers' | 'both'. Drivers by default
  // because the broadcast button lives in the dispatch context.
  const [audience, setAudience] = useState('drivers');
  const [busy, setBusy] = useState(false);

  // Drivers + selection
  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showOnlyOnline, setShowOnlyOnline] = useState(true);
  const pickerRef = useRef(null);

  const triggerCls =
    variant === 'sidebar'
      ? 'w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold px-3 py-2 transition'
      : 'p-2 rounded-full text-neutral-700 hover:bg-neutral-100 transition';

  // Load drivers when modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingDrivers(true);
    axios
      .get(`${API}/admin/drivers`, { headers: authHeaders })
      .then(({ data }) => {
        if (cancelled) return;
        // Only approved/active drivers
        const active = (data || []).filter(
          (d) => d.active !== false && (d.status === 'approved' || !d.status),
        );
        setDrivers(active);
      })
      .catch(() => setDrivers([]))
      .finally(() => !cancelled && setLoadingDrivers(false));
    return () => { cancelled = true; };
  }, [open, authHeaders]);

  // Close picker on outside click
  useEffect(() => {
    if (!pickerOpen) return;
    const onDoc = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [pickerOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (open) return;
    setSelectedIds(new Set());
    setSearch('');
    setPickerOpen(false);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return drivers
      .filter((d) => (showOnlyOnline ? isOnline(d) : true))
      .filter((d) => {
        if (!q) return true;
        return (
          d.name?.toLowerCase().includes(q) ||
          d.phone?.toLowerCase().includes(q) ||
          d.vehicle?.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        // Online first, then by name
        const ao = isOnline(a) ? 0 : 1;
        const bo = isOnline(b) ? 0 : 1;
        if (ao !== bo) return ao - bo;
        return (a.name || '').localeCompare(b.name || '');
      });
  }, [drivers, search, showOnlyOnline]);

  const onlineCount = useMemo(() => drivers.filter(isOnline).length, [drivers]);
  const selectedDrivers = useMemo(
    () => drivers.filter((d) => selectedIds.has(d.id)),
    [drivers, selectedIds],
  );

  const toggleDriver = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filtered.forEach((d) => next.add(d.id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      const baseBody = { title: title.trim() || 'Announcement', text: t };
      const linkTrim = linkUrl.trim();
      if (linkTrim) baseBody.url = linkTrim;

      const requests = [];
      if (audience === 'drivers' || audience === 'both') {
        const body = { ...baseBody };
        if (selectedIds.size > 0) {
          body.driver_ids = Array.from(selectedIds);
        } else {
          body.online_only = onlineOnly;
        }
        requests.push({
          label: 'driver',
          promise: axios.post(`${API}/admin/drivers/broadcast`, body, { headers: authHeaders }),
        });
      }
      if (audience === 'customers' || audience === 'both') {
        // Customer broadcast goes to everyone — no online-only filtering.
        requests.push({
          label: 'customer',
          promise: axios.post(`${API}/admin/customers/broadcast`, baseBody, { headers: authHeaders }),
        });
      }

      const results = await Promise.allSettled(requests.map((r) => r.promise));
      const counts = results.map((r, idx) => ({
        label: requests[idx].label,
        ok: r.status === 'fulfilled',
        count: r.status === 'fulfilled' ? (r.value?.data?.recipients ?? 0) : 0,
      }));
      const total = counts.reduce((s, c) => s + c.count, 0);
      const summary = counts
        .filter((c) => c.ok)
        .map((c) => `${c.count} ${c.label}${c.count === 1 ? '' : 's'}`)
        .join(' · ');
      toast({
        title: total === 0 ? 'No recipients' : `Sent to ${summary}`,
        description: total === 0 ? 'No matching audience right now.' : "They'll receive a push notification.",
      });
      setText('');
      setLinkUrl('');
      setOpen(false);
    } catch (err) {
      toast({ title: 'Broadcast failed', description: err?.response?.data?.detail || 'Try again' });
    } finally {
      setBusy(false);
    }
  };

  const selectedCount = selectedIds.size;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={triggerCls}
        title="Broadcast to drivers"
        aria-label="Broadcast"
        data-testid="broadcast-btn"
      >
        <Megaphone className={variant === 'sidebar' ? 'w-3.5 h-3.5' : 'w-5 h-5'} />
        {variant === 'sidebar' && 'Broadcast'}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="broadcast-modal"
          >
            {/* Uber-style dark header */}
            <div className="px-5 py-4 bg-[#0F172A] text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
                <Radio className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="font-display font-bold text-lg leading-tight">
                  Broadcast to {audience === 'drivers' ? 'drivers' : audience === 'customers' ? 'customers' : 'everyone'}
                </h2>
                <p className="text-[11px] text-white/60 mt-0.5">
                  {audience !== 'customers' && (
                    <>
                      <span className="inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        {onlineCount} online
                      </span>
                      <span className="mx-2 text-white/30">·</span>
                      <span>{drivers.length} active total</span>
                    </>
                  )}
                  {audience === 'customers' && <span>All registered customers</span>}
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-white/60 hover:text-white p-1 rounded-full hover:bg-white/10"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={send} className="p-5 space-y-4">
              {/* Audience selector — Drivers / Customers / Both. Lives at the
                  top of the form because it gates which other widgets render. */}
              <div>
                <label className="text-[11px] font-bold text-neutral-700 uppercase tracking-wider">
                  Send to
                </label>
                <div
                  className="mt-1 inline-flex bg-neutral-100 rounded-full p-0.5 w-full"
                  role="tablist"
                  data-testid="broadcast-audience"
                >
                  {[
                    { id: 'drivers', label: 'Drivers' },
                    { id: 'customers', label: 'Customers' },
                    { id: 'both', label: 'Both' },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      role="tab"
                      aria-selected={audience === opt.id}
                      onClick={() => setAudience(opt.id)}
                      className={`flex-1 px-3 py-1.5 text-xs font-bold rounded-full transition ${
                        audience === opt.id
                          ? 'bg-[#0F172A] text-white shadow-sm'
                          : 'text-neutral-600 hover:text-[#0F172A]'
                      }`}
                      data-testid={`broadcast-audience-${opt.id}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-neutral-500 mt-1.5">
                  {audience === 'drivers'
                    ? 'Pushes to every active driver, online or offline (unless you filter below).'
                    : audience === 'customers'
                    ? 'Pushes to every registered customer profile, regardless of online status.'
                    : 'Pushes to every active driver AND every registered customer in one shot.'}
                </p>
              </div>

              {/* Recipient picker — driver-only. Hidden when broadcasting solely to customers. */}
              {audience !== 'customers' && (
              <div className="relative" ref={pickerRef}>
                <label className="text-[11px] font-bold text-neutral-700 uppercase tracking-wider">
                  Recipients
                </label>
                <button
                  type="button"
                  onClick={() => setPickerOpen((v) => !v)}
                  className="mt-1 w-full min-h-[48px] rounded-2xl border border-neutral-200 hover:border-neutral-300 px-3 py-2 text-left flex items-center gap-2 transition focus:outline-none focus:ring-2 focus:ring-emerald-400/30"
                  data-testid="broadcast-recipients-toggle"
                >
                  <Users className="w-4 h-4 text-neutral-500 shrink-0" />
                  {selectedCount === 0 ? (
                    <span className="flex-1 text-sm text-neutral-500">
                      All {onlineOnly ? 'online' : 'active'} drivers
                      <span className="ml-1.5 text-xs text-neutral-400">
                        ({onlineOnly ? onlineCount : drivers.length})
                      </span>
                    </span>
                  ) : (
                    <div className="flex-1 flex flex-wrap gap-1.5">
                      {selectedDrivers.slice(0, 3).map((d) => (
                        <span
                          key={d.id}
                          className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full pl-1 pr-2 py-0.5"
                        >
                          <span
                            className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] text-white"
                            style={{ background: avatarBg(d.id) }}
                          >
                            {initials(d.name)}
                          </span>
                          {d.name?.split(' ')[0] || 'Driver'}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleDriver(d.id); }}
                            className="hover:text-emerald-900"
                            aria-label={`Remove ${d.name}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                      {selectedCount > 3 && (
                        <span className="inline-flex items-center bg-neutral-100 text-neutral-700 text-xs font-semibold rounded-full px-2 py-0.5">
                          +{selectedCount - 3} more
                        </span>
                      )}
                    </div>
                  )}
                  <ChevronDown className={`w-4 h-4 text-neutral-400 shrink-0 transition ${pickerOpen ? 'rotate-180' : ''}`} />
                </button>

                {pickerOpen && (
                  <div
                    className="absolute z-30 left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-neutral-100 overflow-hidden"
                    data-testid="broadcast-driver-picker"
                  >
                    {/* Search + filter row */}
                    <div className="p-3 border-b border-neutral-100">
                      <div className="relative">
                        <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          autoFocus
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search by name, phone, vehicle…"
                          className="w-full h-10 rounded-xl bg-neutral-50 border border-transparent pl-9 pr-3 text-sm text-[#0F172A] placeholder:text-neutral-400 focus:outline-none focus:bg-white focus:border-neutral-200"
                          data-testid="broadcast-driver-search"
                        />
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs">
                        <button
                          type="button"
                          onClick={() => setShowOnlyOnline((v) => !v)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold transition ${
                            showOnlyOnline ? 'bg-emerald-50 text-emerald-700' : 'bg-neutral-100 text-neutral-600'
                          }`}
                          data-testid="broadcast-filter-online"
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${showOnlyOnline ? 'bg-emerald-500' : 'bg-neutral-400'}`} />
                          {showOnlyOnline ? `Online only · ${onlineCount}` : `Show all · ${drivers.length}`}
                        </button>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={selectAllVisible}
                            className="px-2.5 py-1 rounded-full text-neutral-700 hover:bg-neutral-100 font-semibold"
                            data-testid="broadcast-select-all"
                          >
                            Select all
                          </button>
                          {selectedCount > 0 && (
                            <button
                              type="button"
                              onClick={clearSelection}
                              className="px-2.5 py-1 rounded-full text-rose-600 hover:bg-rose-50 font-semibold"
                              data-testid="broadcast-clear"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Driver list */}
                    <div className="max-h-64 overflow-y-auto">
                      {loadingDrivers ? (
                        <div className="p-6 text-center text-sm text-neutral-500 flex items-center justify-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" /> Loading drivers…
                        </div>
                      ) : filtered.length === 0 ? (
                        <div className="p-8 text-center text-sm text-neutral-500">
                          {showOnlyOnline ? 'No drivers online right now.' : 'No drivers match your search.'}
                        </div>
                      ) : (
                        filtered.map((d) => {
                          const online = isOnline(d);
                          const sel = selectedIds.has(d.id);
                          return (
                            <button
                              key={d.id}
                              type="button"
                              onClick={() => toggleDriver(d.id)}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-neutral-50 transition text-left ${
                                sel ? 'bg-emerald-50/50' : ''
                              }`}
                              data-testid={`broadcast-driver-${d.id}`}
                            >
                              <div className="relative shrink-0">
                                <div
                                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                                  style={{ background: avatarBg(d.id) }}
                                >
                                  {initials(d.name)}
                                </div>
                                <span
                                  className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                                    online ? 'bg-emerald-500' : 'bg-neutral-300'
                                  }`}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-[#0F172A] truncate">
                                  {d.name || 'Driver'}
                                </div>
                                <div className="text-xs text-neutral-500 truncate flex items-center gap-1.5">
                                  {d.vehicle && (
                                    <>
                                      <Car className="w-3 h-3" />
                                      <span className="truncate">{d.vehicle}</span>
                                      <span className="text-neutral-300">·</span>
                                    </>
                                  )}
                                  <span>{d.phone}</span>
                                </div>
                              </div>
                              <span
                                className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                                  online ? 'text-emerald-700 bg-emerald-100' : 'text-neutral-500 bg-neutral-100'
                                }`}
                              >
                                {online ? 'Online' : 'Offline'}
                              </span>
                              <div
                                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition ${
                                  sel
                                    ? 'bg-emerald-500 border-emerald-500'
                                    : 'border-neutral-300'
                                }`}
                              >
                                {sel && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>

                    <div className="px-3 py-2 border-t border-neutral-100 flex justify-end">
                      <button
                        type="button"
                        onClick={() => setPickerOpen(false)}
                        className="rounded-full px-4 py-1.5 text-sm font-semibold bg-[#0F172A] text-white hover:bg-black"
                        data-testid="broadcast-picker-done"
                      >
                        Done {selectedCount > 0 && `(${selectedCount})`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              )}

              <div>
                <label className="text-[11px] font-bold text-neutral-700 uppercase tracking-wider">Title</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={80}
                  className="mt-1 h-10 w-full rounded-xl border border-neutral-200 px-3 text-sm text-[#0F172A] bg-white placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/30"
                  data-testid="broadcast-title"
                />
              </div>
              <div>
                <label className="text-[11px] font-bold text-neutral-700 uppercase tracking-wider">
                  Message <span className="text-neutral-400">({text.length}/500)</span>
                </label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  maxLength={500}
                  rows={4}
                  required
                  placeholder="e.g. Warehouse closing at 5 PM today, please drop completed orders before then."
                  className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm text-[#0F172A] bg-white placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/30"
                  data-testid="broadcast-text"
                />
              </div>

              <div>
                <label className="text-[11px] font-bold text-neutral-700 uppercase tracking-wider">
                  Link <span className="text-neutral-400 font-medium normal-case">(optional — tapping the notification opens this)</span>
                </label>
                <input
                  type="text"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  maxLength={500}
                  placeholder="https://example.com  or  /driver/orders/MR-123456"
                  className="mt-1 h-10 w-full rounded-xl border border-neutral-200 px-3 text-sm text-[#0F172A] bg-white placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/30"
                  data-testid="broadcast-url"
                />
                <p className="text-[10px] text-neutral-500 mt-1">
                  Absolute URLs (https://…) open in a new window; relative paths (/driver/…) navigate inside the app.
                </p>
              </div>

              {/* When no one is hand-picked, allow the legacy 'online only' toggle */}
              {selectedCount === 0 && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={onlineOnly}
                    onChange={(e) => setOnlineOnly(e.target.checked)}
                    className="w-4 h-4 accent-emerald-500"
                    data-testid="broadcast-online-only"
                  />
                  <span className="text-sm text-neutral-700">Online drivers only (seen in last 5 minutes)</span>
                </label>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded-full px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy || !text.trim()}
                  className="rounded-full px-5 py-2 text-sm font-semibold text-white bg-[#0F172A] hover:bg-black inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="broadcast-send"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {selectedCount > 0 ? `Send to ${selectedCount}` : 'Send broadcast'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default BroadcastButton;
